import { stringifyQuery, uuid } from './utils';
import { inIosApp, inAndroidApp } from './env';

const isStaging = process.env.NODE_ENV === 'staging';

/**
 * 创建原生回调函数用于获取原生返回结果
 * @param callback 原生回调
 * @param custName 自定义callback名称
 * @param autoClearAfterCallback 是否在回调执行后自动清理，默认为true
 * @returns {{cbName: string, custName: string, clear: clear}}
 */
export function createAppCallback(
  callback: (...params: any[]) => void,
  custName: string,
  autoClearAfterCallback = true,
): {
  cbName: string;
  custName: string;
  clear: () => void;
} {
  const uid = uuid();
  if (isStaging) console.log('uuid', uid);
  const cbName = `jssdk_cb_${`${custName}_` || ''}${uid.replace(/-/g, '')}`;
  // 清理回调
  function clear() {
    delete window[cbName];
  }
  const cbArg = { cbName, custName, clear };
  window[cbName] = function () {
    // eslint-disable-next-line prefer-rest-params
    const args = Array.prototype.slice.apply(arguments);
    args.push(cbArg); // 将jssdk的相关参数作为回调的最后一个参数传入
    // eslint-disable-next-line prefer-spread
    if (callback) callback.apply(null, args);
    if (autoClearAfterCallback) clear();
  };

  return cbArg;
}

/**
 * 基于iframe的原生schema通讯方式
 * @param schema 原生schema
 * @param options 原生传参
 * @param originRes 是否输出原始结果
 * @returns {Promise<any>}
 */
export function invokeSchemaByIframe(
  schema: string,
  options?: Record<string, string>,
  originRes?: boolean,
  unEncode?: boolean,
): Promise<unknown> {
  const opt = options || {};
  return new Promise((resolve, reject) => {
    const cb = createAppCallback((json: string | Record<string, string>) => {
      const res = typeof json === 'string' ? JSON.parse(json) : json;
      console.log('result by iframe: ', JSON.stringify(res));
      if (originRes) {
        resolve(res);
      } else if (Number(res.code) === 200) {
        // android返回数字，ios返回字符串
        resolve(res.data || {});
      } else {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({ code: res.code, message: res.message });
      }
    }, 'invokeSchema');
    opt.callback = cb.cbName;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-1px';
    iframe.style.top = '-1px';
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    iframe.src = `${schema}?${stringifyQuery(opt, unEncode)}`;
    console.log('schema by iframe: ', iframe.src);
    document.body.appendChild(iframe);
    setTimeout(() => document.body.removeChild(iframe), 3000);
  });
}

/**
 * 基于prompt的原生schema通讯方式
 * @param schema 原生schema
 * @param options 原生传参
 * @param originRes 是否输出原始结果
 * @returns {Promise<any>}
 */
export function invokeSchemaByPrompt(
  schema: string,
  options?: Record<string, string>,
  originRes?: boolean,
  unEncode?: boolean,
): Promise<unknown> {
  const opt = options || {};
  return new Promise((resolve, reject) => {
    const cb = createAppCallback((json: string | Record<string, string>) => {
      const res = typeof json === 'string' ? JSON.parse(json) : json;
      console.log('result by prompt: ', JSON.stringify(res));
      if (originRes) {
        resolve(res);
      } else if (Number(res.code) === 200) {
        // android返回数字，ios返回字符串
        resolve(res.data || {});
      } else {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({ code: res.code, message: res.message });
      }
    }, 'invokeSchema');
    opt.callback = cb.cbName;
    const uri = `${schema}?${stringifyQuery(opt, unEncode)}`;
    console.log('schema by prompt: ', uri);
    prompt(uri);
  });
}

/**
 * ios端jsbridge调用封装
 * @param method 调用方法
 * @param params 调用参数
 * @returns {Promise<any>}
 */
export function invokeIosHandler(
  method: string,
  params?: Record<string, string | boolean>,
  noCallback?: boolean,
): Promise<unknown> {
  return new Promise((resolve) => {
    if (noCallback) {
      if (inIosApp) window.webkit.messageHandlers[method].postMessage(params);
    } else {
      const cb = createAppCallback((json: string | Record<string, string>) => {
        const res = typeof json === 'string' ? JSON.parse(json) : json;
        resolve(res);
      }, 'invokeIosHandler');
      const cbId = `${cb.cbName}_id_${new Date().valueOf()}`;
      const cbParams = {
        callback: cb.cbName,
        callback_id: cbId,
        callbackid: cbId, // 非常旧版本ios使用callbackid参数
      };
      if (inIosApp)
        (window as any).webkit.messageHandlers[method].postMessage({
          ...params,
          ...cbParams,
        });
    }
  });
}

/**
 * android端jsbridge调用封装
 * @param obj 调用对象
 * @param method 调用方法
 * @param params 调用参数
 * @returns {Promise<any>}
 */
type InvokeAndroidHandler = (
  obj: string,
  method: string,
  params?: string | Record<string, string | boolean>,
  noCallback?: boolean,
) => Promise<any>;
export const invokeAndroidHandler: InvokeAndroidHandler = (obj, method, params = {}, noCallback = false) =>
  new Promise((resolve) => {
    if (noCallback) {
      if (inAndroidApp) window[obj][method](params);
    } else {
      const cb = createAppCallback((json: string | Record<string, string>) => {
        const res = typeof json === 'string' ? JSON.parse(json) : json;
        resolve(res);
      }, 'invokeAndroidHandler');
      if (inAndroidApp) window[obj][method](params, cb.cbName, undefined);
    }
  });
